import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { withTheme } from "../theming";
import { userPool } from "../awsConfig";
import { useResponsive } from "../contexts/ResponsiveContext";
import { clearAuthState } from "../utils/authUtils";

// Sibling sites — same list drives portal-landing tiles and header menu.
const SIBLING_LINKS = [
  { name: "Creative", path: "/creative" },
  { name: "Research", path: "/research" },
  { name: "Social", path: "/social" },
  { name: "Business Dev", path: "/business-dev" },
  { name: "Documentation", path: "/documentation" },
  { name: "Admin", path: "/admin" },
  { name: "Founder", path: "/founder" },
];

const Header = ({ isAuthenticated, setIsAuthenticated, theme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { isCompactNav, isPortrait, isPhoneLandscape } = useResponsive();
  const showHamburger = isCompactNav || isPortrait || isPhoneLandscape;

  const handleLogout = () => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) cognitoUser.signOut();
    setIsOpen(false);
    clearAuthState();
    setIsAuthenticated(false);
    window.location.replace("/");
  };

  const handleMenuItemClick = () => setIsOpen(false);

  return (
    <header
      style={{
        backgroundColor: theme.backgroundColor || "#333",
        color: theme.textColor || "#FFF",
        position: "sticky",
        top: 0,
        zIndex: 1000,
      }}
      className="p-4"
    >
      <div className="mx-auto flex justify-between items-center pr-10 pl-5">
        {/* Logo */}
        <div className="flex-shrink-0 mr-8">
          <NavLink
            to="/"
            className={({ isActive }) => {
              const sizeCls = showHamburger ? "text-[135%]" : "text-[170%]";
              return isActive
                ? `font-bold ${sizeCls} hover:opacity-90 focus-visible:opacity-90`
                : `${sizeCls} font-bold hover:opacity-90 focus:opacity-90`;
            }}
            style={{ color: theme.accentColor || "#FFA72A" }}
          >
            X20 Portal
          </NavLink>
        </div>

        {/* Hamburger Menu Icon */}
        <div className={showHamburger ? "block" : "hidden"}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="focus:outline-none p-2 rounded-md"
            aria-label={isOpen ? "Close menu" : "Open menu"}
            aria-expanded={isOpen}
            aria-controls="mobile-nav"
            style={{
              backgroundColor: theme.backgroundColor || "#333",
              color: theme.accentColor || "#FFF",
            }}
          >
            <svg
              className="w-7 h-7"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              {isOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Desktop Menu */}
        <nav
          className={showHamburger ? "hidden" : "flex space-x-4 items-center"}
        >
          {SIBLING_LINKS.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              className={({ isActive }) =>
                isActive
                  ? "font-bold text-[120%] hover:opacity-90 focus-visible:opacity-90"
                  : "text-[120%] hover:opacity-90 focus:opacity-90"
              }
              style={{ color: theme.accentColor || "#FFA72A" }}
            >
              {link.name}
            </NavLink>
          ))}
          {isAuthenticated ? (
            <NavLink
              to="#"
              onClick={handleLogout}
              className="text-[120%] hover:opacity-90 focus:opacity-90"
              style={{ color: theme.accentColor || "#FFA72A" }}
            >
              Logout
            </NavLink>
          ) : (
            <>
              <NavLink
                to="/login"
                className={({ isActive }) =>
                  isActive
                    ? "font-bold text-[120%] hover:opacity-90 focus-visible:opacity-90"
                    : "text-[120%] hover:opacity-90 focus:opacity-90"
                }
                style={{ color: theme.accentColor || "#FFA72A" }}
              >
                Login
              </NavLink>
              <NavLink
                to="/register"
                style={{
                  backgroundColor: theme.accentColor || "#FFA72A",
                  color: theme.buttonTextColor || "#fff",
                  borderRadius: "50px",
                  padding: "6px 20px",
                  fontWeight: 600,
                  fontSize: "1rem",
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                  transition: "opacity 0.2s ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.88")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              >
                Start Free
              </NavLink>
            </>
          )}
        </nav>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <nav
          id="mobile-nav"
          className={`${showHamburger ? "block" : "hidden"} mt-4 max-h-[calc(100vh-80px)] overflow-y-auto`}
          style={{ borderTop: `1px solid ${theme.accentColor || "#FFA72A"}22` }}
        >
          {SIBLING_LINKS.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              onClick={handleMenuItemClick}
              className={({ isActive }) =>
                `block py-3 px-4 text-[120%] border-b ${isActive ? "font-bold" : ""}`
              }
              style={{
                color: theme.accentColor || "#FFA72A",
                borderColor: `${theme.accentColor || "#FFA72A"}1a`,
              }}
            >
              {link.name}
            </NavLink>
          ))}
          {isAuthenticated ? (
            <NavLink
              to="#"
              onClick={handleLogout}
              className="block py-3 px-4 text-[120%]"
              style={{ color: theme.accentColor || "#FFA72A" }}
            >
              Logout
            </NavLink>
          ) : (
            <>
              <NavLink
                to="/login"
                onClick={handleMenuItemClick}
                className={({ isActive }) =>
                  `block py-3 px-4 text-[120%] border-b ${isActive ? "font-bold" : ""}`
                }
                style={{
                  color: theme.accentColor || "#FFA72A",
                  borderColor: `${theme.accentColor || "#FFA72A"}1a`,
                }}
              >
                Login
              </NavLink>
              <NavLink
                to="/register"
                onClick={handleMenuItemClick}
                className="block py-3 px-4 text-[120%] font-semibold"
                style={{ color: theme.accentColor || "#FFA72A" }}
              >
                Start Free
              </NavLink>
            </>
          )}
        </nav>
      )}
    </header>
  );
};

export default withTheme(Header);
